<template>
  <div class="visao-geral">
    <el-row :gutter="10" class="u-mt-10">
      <el-col :xs="24" :sm="12" :md="6">
        <card-kpi
          label="Alunos ativos"
          :value="dados.kpis.alunos_ativos"
          icon="fas fa-user-graduate"
          :loading="loading"
          primary
        />
      </el-col>
      <el-col :xs="24" :sm="12" :md="6">
        <card-kpi
          label="Turmas ativas"
          :value="dados.kpis.turmas_ativas"
          icon="fas fa-users"
          :loading="loading"
          primary
        />
      </el-col>
      <el-col :xs="24" :sm="12" :md="6">
        <card-kpi
          label="Dias letivos"
          :value="diasLetivosLabel"
          :hint="diasLetivosHint"
          icon="fas fa-calendar-alt"
          :loading="loading"
        />
      </el-col>
      <el-col :xs="24" :sm="12" :md="6">
        <card-kpi
          v-if="municipal"
          label="Instituições"
          :value="dados.kpis.instituicoes"
          icon="fas fa-school"
          :loading="loading"
        />
        <card-kpi
          v-else
          label="Lançamentos de frequência"
          :value="dados.frequencia.total_lancamentos"
          icon="fas fa-clipboard-check"
          :loading="loading"
        />
      </el-col>
    </el-row>

    <el-row :gutter="10" class="u-mt-15">
      <el-col :xs="24" :sm="12" :md="6">
        <card-kpi
          label="% Presença geral"
          :value="dados.frequencia.perc_presenca_geral"
          :decimal="1"
          suffix="%"
          icon="fas fa-percentage"
          :loading="loading"
          :warn="dados.frequencia.perc_presenca_geral > 0 && dados.frequencia.perc_presenca_geral < 75"
        />
      </el-col>
      <el-col :xs="24" :sm="12" :md="6">
        <card-kpi
          label="Alunos em risco (>25% faltas)"
          :value="dados.frequencia.alunos_em_risco"
          icon="fas fa-exclamation-triangle"
          :loading="loading"
          :danger="dados.frequencia.alunos_em_risco > 0"
        />
      </el-col>
      <el-col :xs="24" :sm="12" :md="6">
        <card-kpi
          label="Média geral"
          :value="dados.notas.media_geral"
          :decimal="2"
          icon="fas fa-star-half-alt"
          :loading="loading"
        />
      </el-col>
      <el-col :xs="24" :sm="12" :md="6">
        <card-kpi
          label="% Alunos em recuperação"
          :value="dados.notas.perc_recuperacao"
          :decimal="1"
          suffix="%"
          icon="fas fa-redo"
          :loading="loading"
          :warn="dados.notas.perc_recuperacao > 0"
        />
      </el-col>
    </el-row>

    <el-row :gutter="10" class="u-mt-15">
      <el-col :xs="24" :sm="24" :md="12">
        <c-panel title="Presença x Faltas" titleIcon="fas fa-chart-pie">
          <div class="chart-box chart-box--md">
            <doughnut-chart
              v-if="!loading"
              class="chart-canvas"
              :chart-data="chartPresenca"
            />
            <div v-else class="empty">Carregando...</div>
          </div>
        </c-panel>
      </el-col>
      <el-col :xs="24" :sm="24" :md="12">
        <c-panel title="Total de lançamentos" titleIcon="fas fa-chart-bar">
          <div class="chart-box chart-box--md">
            <bar-chart
              v-if="!loading"
              class="chart-canvas"
              :chart-data="chartLancamentos"
            />
            <div v-else class="empty">Carregando...</div>
          </div>
        </c-panel>
      </el-col>
    </el-row>

    <div class="atualizado-em" v-if="dados.atualizado_em">
      <i class="fas fa-clock"></i>
      Atualizado em {{ formatDateTime(dados.atualizado_em) }}
    </div>
  </div>
</template>

<script>
export default {
  name: 'VisaoGeral',
  components: {
    CardKpi: () => import('../shared/CardKpi.vue'),
    cPanel: () => import('@/shared/c-panel/c-panel.vue'),
    DoughnutChart: () => import('@/chart/DoughnutChart'),
    BarChart: () => import('@/chart/BarChart')
  },
  props: {
    periodoId: { type: [Number, String], default: null },
    instituicaoId: { type: [Number, String], default: null },
    municipal: { type: Boolean, default: false }
  },
  data: () => ({
    loading: false,
    dados: {
      kpis: {
        alunos_ativos: 0,
        turmas_ativas: 0,
        instituicoes: 0,
        dias_letivos_decorridos: 0,
        dias_letivos_total: 0
      },
      frequencia: {
        perc_presenca_geral: 0,
        alunos_em_risco: 0,
        total_lancamentos: 0
      },
      notas: {
        perc_recuperacao: 0,
        media_geral: 0,
        total_notas: 0
      },
      atualizado_em: null
    }
  }),
  computed: {
    diasLetivosLabel () {
      const d = this.dados.kpis.dias_letivos_decorridos
      const t = this.dados.kpis.dias_letivos_total
      if (!t) return '—'
      return `${d} / ${t}`
    },
    diasLetivosHint () {
      const t = this.dados.kpis.dias_letivos_total
      if (!t) return ''
      const perc = Math.round((this.dados.kpis.dias_letivos_decorridos / t) * 100)
      return `${perc}% do período letivo`
    },
    chartPresenca () {
      const p = Number(this.dados.frequencia.perc_presenca_geral || 0)
      const f = Math.max(0, 100 - p)
      return {
        labels: ['Presenças', 'Faltas'],
        datasets: [{
          data: [p, f],
          backgroundColor: ['#50964d', '#d0021b']
        }]
      }
    },
    chartLancamentos () {
      return {
        labels: ['Frequência', 'Notas'],
        datasets: [{
          label: 'Total de registros',
          data: [
            Number(this.dados.frequencia.total_lancamentos || 0),
            Number(this.dados.notas.total_notas || 0)
          ],
          backgroundColor: ['#346daa', '#9013fe']
        }]
      }
    }
  },
  watch: {
    periodoId () { this.load() },
    instituicaoId () { this.load() },
    municipal () { this.load() }
  },
  mounted () {
    this.load()
  },
  methods: {
    load () {
      if (!this.periodoId) return
      if (!this.municipal && !this.instituicaoId) return

      this.loading = true
      const base = this.$store.state.config.baseUrlApi
      let url
      let payload = {}

      if (this.municipal) {
        url = `${base}/api/dashboard-diario/municipal/visao-geral/${this.periodoId}`
        if (this.instituicaoId) payload.instituicao_id = this.instituicaoId
      } else {
        url = `${base}/api/dashboard-diario/visao-geral/${this.instituicaoId}/${this.periodoId}`
      }

      this.$axios.post(url, payload, this.$store.getters.getHeaderAuth)
        .then(response => {
          this.dados = Object.assign({}, this.dados, response.data || {})
          this.loading = false
        })
        .catch(error => {
          this.loading = false
          this.$store.commit('throwException', error)
        })
    },
    formatDateTime (iso) {
      if (!iso) return ''
      try {
        return this.$moment(iso).format('DD/MM/YYYY HH:mm')
      } catch (e) {
        return iso
      }
    }
  }
}
</script>

<style scoped>
.chart-box { position: relative; width: 100%; overflow: hidden; height: 280px; }
.chart-canvas { display: block; width: 100% !important; height: 100% !important; }
.chart-box ::v-deep canvas { width: 100% !important; height: 100% !important; }
.empty {
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: center;
  color: rgba(0, 0, 0, 0.45);
}
.atualizado-em {
  font-size: 11px;
  color: rgba(0, 0, 0, 0.45);
  text-align: right;
  margin-top: 12px;
  padding-right: 6px;
}
</style>
